import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { Component, computed, effect, ElementRef, inject, untracked, viewChild } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { isNil } from 'lodash';
import { debounceTime, map, startWith, switchMap } from 'rxjs';

import { GenericObjectType } from '@models/helpers';
import { FieldDataSourceSystemDictionaries } from '@models/module/fieldDataSourceType';
import { CipoControlBase } from '@shared/components/fields/cipo-control.base';
import { CipoListConfig, CipoListOptionViewType } from '@shared/components/fields/common';
import { DEBOUNCE_TIMES } from '@shared/consts';
import { LIST_SEARCH_THRESHOLD } from '@shared/consts/global.consts';
import { CipoFormModule } from '@shared/modules/forms.module';
import { CipoAddressService } from '../cipo-address/cipo-address.service';
import { CipoTextViewComponent } from '../cipo-text-view/cipo-text-view.component';

@Component({
  selector: 'cipo-select',
  imports: [CipoFormModule, CipoTextViewComponent, ScrollingModule],
  templateUrl: './cipo-select.component.html',
})
export class CipoSelectComponent extends CipoControlBase<string | string[], CipoListConfig> {
  addressService = inject(CipoAddressService);

  searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');
  scrollingViewport = viewChild(CdkVirtualScrollViewport);

  readonly LIST_SEARCH_THRESHOLD = LIST_SEARCH_THRESHOLD;
  readonly ITEM_SIZE = 48; // This is the height of each item in pixels

  searchControl = new FormControl('');
  CipoListOptionViewType = CipoListOptionViewType;

  controlTouchedEffect = effect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const touched = this.controlTouched();
    untracked(() => {
      this.checkOptions();
    });
  });

  /** Always string[], to avoid checking everytime if it's multiple or not */
  controlValue = toSignal<string[]>(
    toObservable(this.control).pipe(
      switchMap(ctrl => {
        const multiple = this.fieldData()?.multiple;
        const initialValue = multiple ? ((ctrl.value ?? []) as string[]) : ctrl.value ? [ctrl.value as string] : [];
        return ctrl.valueChanges.pipe(
          /* If the control is not multiple, wrap the value in an array */
          map(val => (multiple ? val : val ? [val] : [])),
          startWith(initialValue),
        );
      }),
    ),
  );

  /** This returns a single value always, to use when the control is not multiple */
  controlSingleValue = computed(() => {
    const value = this.controlValue();
    return value?.length ? value[0] : null;
  });

  /** This returns the value of the first option */
  firstOptionValue = computed(() => {
    const value = this.controlSingleValue();
    return this.optionsDict()[value] ?? value;
  });

  allOptionsSelected = computed(() => {
    // selected options do not include disabled options or options excluded from select-all
    const selectedOptions = this.controlValue();
    /* If the user is searching - never display all selected label */
    if (!selectedOptions?.length || this.searchControl.value) {
      return false;
    }
    return (
      selectedOptions?.length === this.filteredOptions().filter(o => !o.isDisabled && !o.excludeFromSelectAll)?.length
    );
  });

  selectedOptionsValue = computed(() => {
    const value = this.controlValue();
    return value?.map(option => this.optionsDict()[option]).join(', ');
  });

  optionsDict = computed(() => {
    const options = this.deepFieldData.options();
    const hasOptions = options?.length > 0;
    if (!hasOptions) {
      return {};
    }
    return options.reduce((acc, option) => {
      acc[option.key] = option.value;
      return acc;
    }, {} as GenericObjectType);
  });

  /* TODO: We should have the same logic on all search elements (in autocomplete we have signal search) */
  private readonly searchControlValue = toSignal(
    this.searchControl.valueChanges.pipe(debounceTime(DEBOUNCE_TIMES.short)),
  );

  //   we might want to use pagination in the future
  filteredOptions = computed(() => {
    const searchValue = this.searchControlValue();
    const selectedOptions = this.controlValue();
    const options = this.deepFieldData.options() || [];
    if (!searchValue) {
      return options;
    }
    return options.filter(
      option => option.value.toLowerCase().includes(searchValue.toLowerCase()) || selectedOptions.includes(option.key),
    );
  });

  constructor() {
    super();
    effect(() => {
      if (isNil(this.deepFieldData.options()) && untracked(this.fieldData)?.dataSourceInfo?.id) {
        untracked(() => this.loadDataSource());
      }
    });
  }

  allOptionsDisabled = computed(() => this.filteredOptions()?.every(o => o.isDisabled));

  toggleAllIcon = computed(() => {
    const selected = this.controlValue();
    const enabledOptions = this.filteredOptions().filter(o => !o.isDisabled);
    if (!selected?.length) return 'check_box_outline_blank';
    if (selected.length >= enabledOptions.length) return 'check_box';
    return 'indeterminate_check_box';
  });

  virtualScrollingHeight = computed(() => {
    const filteredOptionsLength = this.filteredOptions()?.length || 0;
    const { multiple, required, hideSearch } = this.fieldData();
    const maxItems = hideSearch ? 5 : 4;
    // Reserve full height while loading so the CDK overlay picks the correct position from the start
    if (!this.deepFieldData.options()) {
      return maxItems * this.ITEM_SIZE;
    }
    const showNoneLabel = !required && !multiple && !this.searchControl.value;
    const showAllMultiple = !!multiple && !this.searchControl.value && !this.fieldData().toggleAllOutside;
    if (filteredOptionsLength === 0 || showNoneLabel || (showAllMultiple && !this.allOptionsDisabled())) {
      // for 0 => +1 for 'nothing found' option
      // for showNoneLabel => +1 for 'none' option
      // for multiple & not searching => +1 for 'select all' option
      return Math.min(filteredOptionsLength + 1, maxItems) * this.ITEM_SIZE;
    }
    return Math.min(filteredOptionsLength, maxItems) * this.ITEM_SIZE;
  });

  hasAddressesDataSource = computed(
    () =>
      this.fieldData()?.dataSourceInfo?.id === FieldDataSourceSystemDictionaries.Addresses ||
      this.fieldData()?.dataSourceInfo?.id === FieldDataSourceSystemDictionaries.GlobalAddresses,
  );

  /**
   * The search input lives inside the mat-select panel, so its keydown events bubble to the
   * panel's own handler. Keep printable keys local to the search box, but forward the
   * navigation keys so the option list stays keyboard-operable (WCAG 2.1.1) and Tab/Escape
   * close the panel and restore focus to the trigger instead of escaping to the browser.
   */
  private readonly SELECT_NAVIGATION_KEYS = ['ArrowDown', 'ArrowUp', 'Enter', 'Tab', 'Escape'];
  onSearchKeydown(event: KeyboardEvent) {
    if (!this.SELECT_NAVIGATION_KEYS.includes(event.key)) {
      event.stopPropagation();
    }
  }

  onOpen() {
    this.scrollingViewport()?.scrollToIndex(0);
    this.scrollingViewport()?.checkViewportSize();
    // defer focus so the panel overlay is fully in the DOM before focusing
    if (this.searchInput()) {
      setTimeout(() => this.searchInput()?.nativeElement.focus());
    }
    this.loadDataSource();
    this.valueOnFocus = this.controlValue()?.join(',') ?? '';
  }

  onClose() {
    const currentValue = this.controlValue()?.join(',') ?? '';
    if (this.valueOnFocus !== currentValue) {
      this.control().markAsTouched();
      this.onblur.emit();
    }
  }

  toggleSelectAllOptions(checked: boolean) {
    // disabled options and options excluded from select-all are never toggled
    const newValue = checked
      ? [
          ...this.fieldData()
            ?.options.filter(o => !o.isDisabled && !o.excludeFromSelectAll)
            .map(o => o.key),
        ]
      : [];

    // update the selectedOptions because there are cases when control valueChanges is not emitted and selectedOptions is not updated
    this.control().setValue(newValue);
  }

  trackByOptionKey(index: number, option: { key: string; value: string; isDisabled?: boolean }) {
    return option.key;
  }

  openMap($event: MouseEvent) {
    $event.stopPropagation();
    this.addressService
      .getAddressAndOpenMap(this.fieldData()?.label ?? '', this.control()?.value as unknown as number)
      .subscribe();
  }
}
